import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Mail, Phone, MapPin, Clock, ChevronDown, ChevronUp, Upload, X, Plus, Minus, FileText } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import HCaptcha from "@hcaptcha/react-hcaptcha";

interface AnimalStrain {
  strain_name: string;
  strain_number: string;
  gender: string;
  age_weeks: string;
  quantity: string;
  delivery_date: string;
  additional_notes: string;
}

const Contact = () => {
  const [activeForm, setActiveForm] = useState<'consultation' | 'quote'>('consultation');
  
  // Function to handle form type change and reset captcha
  const handleFormTypeChange = (formType: 'consultation' | 'quote') => {
    setActiveForm(formType);
    // Reset captcha tokens when switching forms
    setConsultationCaptchaToken(null);
    setQuoteCaptchaToken(null);
  };
  
  // Contact information for both forms
  const [formData, setFormData] = useState({
    name: "",
    institution: "",
    pi_name: "",
    email: "",
    mobile_phone: "",
    landline_phone: ""
  });

  // Consultation form specific
  const [consultationContent, setConsultationContent] = useState("");

  // Animal strains (multiple) for quote form
  const [animalStrains, setAnimalStrains] = useState<AnimalStrain[]>([{
    strain_name: "",
    strain_number: "",
    gender: "",
    age_weeks: "",
    quantity: "",
    delivery_date: "",
    additional_notes: ""
  }]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [isPolicyOpen, setIsPolicyOpen] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  
  // Rate limiting and spam protection state
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  const [submissionCount, setSubmissionCount] = useState<number>(0);
  const [isRateLimited, setIsRateLimited] = useState<boolean>(false);
  const [rateLimitMessage, setRateLimitMessage] = useState<string>('');
  
  // Captcha states
  const [consultationCaptchaToken, setConsultationCaptchaToken] = useState<string | null>(null);
  const [quoteCaptchaToken, setQuoteCaptchaToken] = useState<string | null>(null);

  // Check URL parameter to set initial form type
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const formType = urlParams.get('type');
    if (formType === 'consultation') {
      setActiveForm('consultation');
    } else if (formType === 'quote') {
      setActiveForm('quote');
    }
    
    // Load rate limiting state from localStorage
    const lastSubmission = localStorage.getItem('lastContactSubmission');
    const submissionCountStr = localStorage.getItem('contactSubmissionCount');
    
    if (lastSubmission) {
      const timeSinceLastSubmission = Date.now() - parseInt(lastSubmission);
      const hoursSinceLastSubmission = timeSinceLastSubmission / (1000 * 60 * 60);
      
      if (hoursSinceLastSubmission < 1) {
        const count = parseInt(submissionCountStr || '0');
        setSubmissionCount(count);
        setLastSubmissionTime(parseInt(lastSubmission));
        
        if (count >= 3) {
          setIsRateLimited(true);
          setRateLimitMessage(`너무 많은 제출 시도입니다. ${Math.ceil(60 - (timeSinceLastSubmission / (1000 * 60)))}분 후에 다시 시도해주세요.`);
        }
      } else {
        // Reset count after an hour
        setSubmissionCount(0);
        setIsRateLimited(false);
        localStorage.removeItem('contactSubmissionCount');
      }
    }
  }, []);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleStrainChange = (index: number, field: keyof AnimalStrain, value: string) => {
    setAnimalStrains(prev => prev.map((strain, i) => 
      i === index ? { ...strain, [field]: value } : strain
    ));
  };

  const addStrain = () => {
    setAnimalStrains(prev => [...prev, {
      strain_name: "",
      strain_number: "",
      gender: "",
      age_weeks: "",
      quantity: "",
      delivery_date: "",
      additional_notes: ""
    }]);
  };

  const removeStrain = (index: number) => {
    if (animalStrains.length > 1) {
      setAnimalStrains(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    // Check if adding these files would exceed 5 files total
    if (attachments.length + files.length > 5) {
      toast({
        title: "파일 개수 초과",
        description: "최대 5개의 파일까지 첨부할 수 있습니다.",
        variant: "destructive",
      });
      return;
    }
    
    // Check each file size (max 10MB)
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "파일 크기 초과",
          description: `${file.name} 파일 크기는 10MB 이하로 제한됩니다.`,
          variant: "destructive",
        });
        return;
      }
    }
    
    setAttachments(prev => [...prev, ...files]);
    
    // Reset the file input
    const fileInput = document.getElementById('file-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const removeFile = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFile = async (file: File): Promise<{ path: string; filename: string } | null> => {
    try {
      // Get auth session for secure upload
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Authentication required for file upload');
      }

      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('filename', file.name);

      // Call secure upload edge function with magic number verification
      const response = await fetch(
        `https://nbdkpnrohpqqqqqnmhit.supabase.co/functions/v1/secure-contact-upload`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const result = await response.json();
      return { path: result.path, filename: result.filename };
    } catch (error) {
      console.error('File upload error:', error);
      toast({
        title: "파일 업로드 실패",
        description: `${file.name} 파일 업로드 중 오류가 발생했습니다.`,
        variant: "destructive",
      });
      return null;
    }
  };

  const handleConsultationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Form submission started
    
    // Rate limiting check
    if (isRateLimited) {
      toast({
        title: "제출 제한",
        description: rateLimitMessage,
        variant: "destructive",
      });
      return;
    }
    
    // Client-side rate limiting (3 submissions per hour)
    const now = Date.now();
    const timeSinceLastSubmission = now - lastSubmissionTime;
    const minutesSinceLastSubmission = timeSinceLastSubmission / (1000 * 60);
    
    if (minutesSinceLastSubmission < 20 && submissionCount >= 3) {
      const waitMinutes = Math.ceil(20 - minutesSinceLastSubmission);
      setIsRateLimited(true);
      setRateLimitMessage(`너무 빠른 제출입니다. ${waitMinutes}분 후에 다시 시도해주세요.`);
      toast({
        title: "제출 제한",
        description: `너무 빠른 제출입니다. ${waitMinutes}분 후에 다시 시도해주세요.`,
        variant: "destructive",
      });
      return;
    }
    
    if (!privacyAgreed) {
      console.log('개인정보 동의 필요');
      toast({
        title: "개인정보 수집 및 이용 동의 필요",
        description: "개인정보 수집 및 이용에 동의해주세요.",
        variant: "destructive",
      });
      return;
    }
    
    // Captcha validation
    if (!consultationCaptchaToken) {
      toast({
        title: "보안 확인 필요",
        description: "로봇이 아님을 확인해주세요.",
        variant: "destructive",
      });
      return;
    }
    
    // PI 성함 필수 검증
    if (!formData.pi_name || formData.pi_name.trim() === '') {
      console.log('PI 성함 필요');
      toast({
        title: "PI 성함 입력 필요",
        description: "PI 성함을 입력해주세요.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    if (attachments.length > 0) {
      setIsUploading(true);
    }

    try {
      let attachmentUrls: string[] = [];
      let attachmentFilenames: string[] = [];

      // Upload all files if any are selected
      if (attachments.length > 0) {
        for (const file of attachments) {
          const uploadResult = await uploadFile(file);
          if (uploadResult) {
            attachmentUrls.push(uploadResult.path);
            attachmentFilenames.push(uploadResult.filename);
          }
        }
      }

      // Save consultation to database with attachments
      const dbData = {
        name: formData.name,
        institution: formData.institution,
        pi_name: formData.pi_name || null,
        email: formData.email,
        mobile_phone: formData.mobile_phone,
        landline_phone: formData.landline_phone || null,
        consultation_content: consultationContent,
        attachment_urls: attachmentUrls.length > 0 ? attachmentUrls : null,
        attachment_filenames: attachmentFilenames.length > 0 ? attachmentFilenames : null,
        submission_type: 'consultation'
      };
      
      const { error } = await supabase
        .from('contact_submissions')
        .insert([dbData]);

      if (error) {
        throw error;
      }

      // Send email notification for consultation with attachments
      try {
        const emailData = {
          ...formData,
          consultation_content: consultationContent,
          attachment_filenames: attachmentFilenames,
          attachment_urls: attachmentUrls,
          has_attachments: attachmentFilenames.length > 0,
          submission_type: 'consultation',
          captcha_token: consultationCaptchaToken
        };
        
        const emailResponse = await supabase.functions.invoke('send-contact-notification', {
          body: emailData
        });

        if (emailResponse.error) {
          // Don't fail the whole submission if email fails
          console.error('Email notification failed');
        }
      } catch (emailError) {
        console.error('Email notification failed');
        // Don't fail the whole submission if email fails
      }
      toast({
        title: "상담 문의가 성공적으로 전송되었습니다",
        description: "곧 연락드리겠습니다. 감사합니다.",
      });

      // Update rate limiting tracking
      const now = Date.now();
      const newCount = submissionCount + 1;
      setLastSubmissionTime(now);
      setSubmissionCount(newCount);
      localStorage.setItem('lastContactSubmission', now.toString());
      localStorage.setItem('contactSubmissionCount', newCount.toString());
      
      // Check if user should be rate limited
      if (newCount >= 3) {
        setIsRateLimited(true);
        setRateLimitMessage('1시간 내 최대 3회까지 제출 가능합니다. 잠시 후 다시 시도해주세요.');
      }

      // Reset form
      setFormData({
        name: "",
        institution: "",
        pi_name: "",
        email: "",
        mobile_phone: "",
        landline_phone: ""
      });
      setConsultationContent("");
      setAttachments([]);
      setPrivacyAgreed(false);
      setConsultationCaptchaToken(null);
      
      // Reset file input
      const fileInput = document.getElementById('consultation-file-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error) {
      toast({
        title: "오류가 발생했습니다",
        description: "상담 문의를 다시 시도해주세요.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setIsUploading(false);
    }
  };

  const handleQuoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Rate limiting check
    if (isRateLimited) {
      toast({
        title: "제출 제한",
        description: rateLimitMessage,
        variant: "destructive",
      });
      return;
    }
    
    // Client-side rate limiting check
    const now = Date.now();
    const timeSinceLastSubmission = now - lastSubmissionTime;
    const minutesSinceLastSubmission = timeSinceLastSubmission / (1000 * 60);
    
    if (minutesSinceLastSubmission < 20 && submissionCount >= 3) {
      const waitMinutes = Math.ceil(20 - minutesSinceLastSubmission);
      setIsRateLimited(true);
      setRateLimitMessage(`너무 빠른 제출입니다. ${waitMinutes}분 후에 다시 시도해주세요.`);
      toast({
        title: "제출 제한",
        description: `너무 빠른 제출입니다. ${waitMinutes}분 후에 다시 시도해주세요.`,
        variant: "destructive",
      });
      return;
    }
    
    if (!privacyAgreed) {
      toast({
        title: "개인정보 수집 및 이용 동의 필요",
        description: "개인정보 수집 및 이용에 동의해주세요.",
        variant: "destructive",
      });
      return;
    }
    
    // Captcha validation
    if (!quoteCaptchaToken) {
      toast({
        title: "보안 확인 필요",
        description: "로봇이 아님을 확인해주세요.",
        variant: "destructive",
      });
      return;
    }
    
    // Validate required strain fields
    for (let i = 0; i < animalStrains.length; i++) {
      const strain = animalStrains[i];
      if (!strain.strain_name || strain.strain_name.trim() === '') {
        toast({
          title: "계통명 입력 필요",
          description: `계통 ${i + 1}의 계통명을 입력해주세요.`,
          variant: "destructive",
        });
        return;
      }
      if (!strain.strain_number || strain.strain_number.trim() === '') {
        toast({
          title: "계통번호 입력 필요",
          description: `계통 ${i + 1}의 계통번호를 입력해주세요.`,
          variant: "destructive",
        });
        return;
      }
    }
    
    setIsSubmitting(true);
    setIsUploading(true);

    try {
      let attachmentUrls: string[] = [];
      let attachmentFilenames: string[] = [];

      // Upload all files if any are selected
      if (attachments.length > 0) {
        for (const file of attachments) {
          const uploadResult = await uploadFile(file);
          if (uploadResult) {
            attachmentUrls.push(uploadResult.path);
            attachmentFilenames.push(uploadResult.filename);
          }
        }
      }

      // Save to database with new animal_strains structure
      const { error } = await supabase
        .from('contact_submissions')
        .insert([{
          name: formData.name,
          institution: formData.institution,
          pi_name: formData.pi_name || null,
          email: formData.email,
          mobile_phone: formData.mobile_phone,
          landline_phone: formData.landline_phone || null,
          // New separate strain fields
          strain_name: animalStrains[0]?.strain_name || null,
          strain_number: animalStrains[0]?.strain_number || null,
          // Legacy fields for backward compatibility (using first strain)
          strain_info: animalStrains[0] ? `${animalStrains[0].strain_name} / ${animalStrains[0].strain_number}` : null,
          gender: animalStrains[0]?.gender || null,
          age_weeks: animalStrains[0]?.age_weeks || null,
          quantity: animalStrains[0]?.quantity ? parseInt(animalStrains[0].quantity) : null,
          delivery_date: animalStrains[0]?.delivery_date || null,
          additional_notes: animalStrains[0]?.additional_notes || null,
          // New animal strains array
          animal_strains: animalStrains.map(strain => ({
            ...strain,
            quantity: strain.quantity ? parseInt(strain.quantity) : null
          })),
          attachment_urls: attachmentUrls.length > 0 ? attachmentUrls : null,
          attachment_filenames: attachmentFilenames.length > 0 ? attachmentFilenames : null,
          // Keep old fields for backward compatibility
          attachment_url: attachmentUrls[0] || null,
          attachment_filename: attachmentFilenames[0] || null,
          submission_type: 'quote'
        }]);

      if (error) throw error;

      // Send email notification
      try {
        const emailData = {
          ...formData,
          animal_strains: animalStrains,
          attachment_filenames: attachmentFilenames,
          attachment_urls: attachmentUrls,
          has_attachments: attachmentFilenames.length > 0,
          // Legacy fields for email template compatibility
          strain_info: animalStrains[0] ? `${animalStrains[0].strain_name} / ${animalStrains[0].strain_number}` : "",
          gender: animalStrains[0]?.gender || "",
          age_weeks: animalStrains[0]?.age_weeks || "",
          quantity: animalStrains[0]?.quantity || "",
          delivery_date: animalStrains[0]?.delivery_date || "",
          additional_notes: animalStrains.map(strain => strain.additional_notes).filter(Boolean).join('\n'),
          attachment_filename: attachmentFilenames[0] || null,
          attachment_url: attachmentUrls[0] || null,
          has_attachment: attachmentFilenames.length > 0,
          submission_type: 'quote',
          captcha_token: quoteCaptchaToken
        };
        
        const emailResponse = await supabase.functions.invoke('send-contact-notification', {
          body: emailData
        });

        if (emailResponse.error) {
          console.error('Email notification failed');
          // Don't fail the whole submission if email fails
        }
      } catch (emailError) {
        console.error('Failed to send email notification:', emailError);
        // Don't fail the whole submission if email fails
      }

      toast({
        title: "견적 요청이 성공적으로 전송되었습니다",
        description: "곧 연락드리겠습니다. 감사합니다.",
      });

      // Update rate limiting tracking
      const now = Date.now();
      const newCount = submissionCount + 1;
      setLastSubmissionTime(now);
      setSubmissionCount(newCount);
      localStorage.setItem('lastContactSubmission', now.toString());
      localStorage.setItem('contactSubmissionCount', newCount.toString());
      
      // Check if user should be rate limited
      if (newCount >= 3) {
        setIsRateLimited(true);
        setRateLimitMessage('1시간 내 최대 3회까지 제출 가능합니다. 잠시 후 다시 시도해주세요.');
      }

      // Reset form
      setFormData({
        name: "",
        institution: "",
        pi_name: "",
        email: "",
        mobile_phone: "",
        landline_phone: ""
      });
      setAnimalStrains([{
        strain_name: "",
        strain_number: "",
        gender: "",
        age_weeks: "",
        quantity: "",
        delivery_date: "",
        additional_notes: ""
      }]);
      setAttachments([]);
      setPrivacyAgreed(false);
      setQuoteCaptchaToken(null);
      
      // Reset file input
      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "오류가 발생했습니다",
        description: "견적 요청을 다시 시도해주세요.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setIsUploading(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-background to-muted/20">
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            문의하기
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            상담 및 견적을 위해 전문가 팀에 문의하세요.
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info Section - Left Panel */}
          <div>
            <Card className="shadow-lg border-0 bg-card/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl">연락처 정보</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground mb-1">전화</div>
                    <div className="text-muted-foreground">+82-2-577-1684</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground mb-1">팩스</div>
                    <div className="text-muted-foreground">+82-2-577-1865</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground mb-1">이메일</div>
                    <div className="text-muted-foreground">info@CentralTG.co.kr</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground mb-1">주소</div>
                    <div className="text-muted-foreground text-sm">
                      서울특별시 서초구 서초대로 396,<br />
                      강남빌딩 2층 222호 (우)06619
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground mb-1">운영시간</div>
                    <div className="text-muted-foreground text-sm">
                      월 - 금: 오전 8시 - 오후 5시 30분
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Form Section - Right Panel */}
          <div>
            {/* Form Type Selector */}
            <div className="mb-6">
              <div className="inline-flex p-1 bg-muted rounded-lg shadow-inner w-full">
                <Button
                  type="button"
                  variant={activeForm === 'consultation' ? 'default' : 'ghost'}
                  onClick={() => handleFormTypeChange('consultation')}
                  className="flex-1 py-3 text-base font-medium transition-all duration-200"
                >
                  일반 상담
                </Button>
                <Button
                  type="button"
                  variant={activeForm === 'quote' ? 'default' : 'ghost'}
                  onClick={() => handleFormTypeChange('quote')}
                  className="flex-1 py-3 text-base font-medium transition-all duration-200"
                >
                  견적 요청
                </Button>
              </div>
            </div>
        
        {/* Forms */}
        <div>
          {activeForm === 'consultation' ? (
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="text-2xl">일반 상담</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleConsultationSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="consultation-name">이름 <span className="text-destructive">*</span></Label>
                    <Input 
                      id="consultation-name"
                      placeholder="ex) 홍길동" 
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="mt-2"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="consultation-institution">회사 및 기관명 <span className="text-destructive">*</span></Label>
                    <Input 
                      id="consultation-institution"
                      placeholder="ex) OO 대학교" 
                      value={formData.institution}
                      onChange={(e) => handleInputChange('institution', e.target.value)}
                      className="mt-2"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="consultation-pi-name">PI 성함 <span className="text-destructive">*</span></Label>
                    <Input 
                      id="consultation-pi-name"
                      placeholder="ex) OOO 교수님" 
                      value={formData.pi_name}
                      onChange={(e) => handleInputChange('pi_name', e.target.value)}
                      className="mt-2"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="consultation-email">이메일주소 <span className="text-destructive">*</span></Label>
                    <Input 
                      id="consultation-email"
                      type="email" 
                      placeholder="ex) info@centraltg.co.kr" 
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="mt-2"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="consultation-mobile">연락처(휴대폰) <span className="text-destructive">*</span></Label>
                      <Input 
                        id="consultation-mobile"
                        placeholder="ex) 010-1234-5678" 
                        value={formData.mobile_phone}
                        onChange={(e) => handleInputChange('mobile_phone', e.target.value)}
                        className="mt-2"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="consultation-landline">연락처(회사)</Label>
                      <Input 
                        id="consultation-landline"
                        placeholder="ex) 02-1234-5678" 
                        value={formData.landline_phone}
                        onChange={(e) => handleInputChange('landline_phone', e.target.value)}
                        className="mt-2"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="consultation-content">문의 내용 <span className="text-destructive">*</span></Label>
                    <Textarea 
                      id="consultation-content"
                      placeholder="문의 내용을 입력해주세요." 
                      className="min-h-[120px] mt-2"
                      value={consultationContent}
                      onChange={(e) => setConsultationContent(e.target.value)}
                      required
                    />
                  </div>
                  
                  {/* File Upload Section for Consultation */}
                  <div className="border-t pt-6">
                    <div className="space-y-4">
                      <Label htmlFor="consultation-file-upload">파일 첨부 (선택)</Label>
                      <div className="space-y-3">
                        <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg border">
                          <p className="font-medium mb-2">파일 업로드 제한사항</p>
                          <ul className="space-y-1 list-disc list-inside">
                            <li><strong>파일 개수:</strong> 최대 5개</li>
                            <li><strong>파일 크기:</strong> 각 파일당 최대 10MB</li>
                          </ul>
                        </div>
                        {attachments.length === 0 ? (
                          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
                            <Upload className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                            <div className="space-y-2">
                              <p className="text-sm text-muted-foreground">
                                파일을 끌어다 놓거나 클릭하여 업로드하세요
                              </p>
                            </div>
                            <Input
                              id="consultation-file-upload"
                              type="file"
                              multiple
                              className="hidden"
                              onChange={handleFileChange}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              className="mt-4"
                              onClick={() => document.getElementById('consultation-file-upload')?.click()}
                              disabled={isUploading}
                            >
                              파일 선택
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {attachments.map((file, index) => (
                              <div key={index} className="flex items-center justify-between p-3 border rounded-lg bg-muted/50">
                                <div className="flex items-center space-x-3">
                                  <div className="p-2 bg-primary/10 rounded">
                                    <Upload className="h-4 w-4 text-primary" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium">{file.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {(file.size / 1024 / 1024).toFixed(1)}MB
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeFile(index)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => document.getElementById('consultation-file-upload')?.click()}
                              disabled={isUploading || attachments.length >= 5}
                              className="w-full"
                            >
                              {attachments.length >= 5 ? '최대 파일 개수에 도달했습니다' : '추가 파일 선택'}
                            </Button>
                            <Input
                              id="consultation-file-upload"
                              type="file"
                              multiple
                              className="hidden"
                              onChange={handleFileChange}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t pt-6">
                    <div className="space-y-4">
                      <Collapsible open={isPolicyOpen} onOpenChange={setIsPolicyOpen}>
                        <CollapsibleTrigger asChild>
                          <Button 
                            type="button" 
                            variant="outline" 
                            className="w-full justify-between"
                          >
                            개인정보 수집 및 이용 동의 / 처리방침
                            {isPolicyOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-4">
                          <div className="max-h-64 overflow-y-auto p-4 border rounded-md bg-muted/50 text-sm">
                            <div className="space-y-4">
                              <div>
                                <h4 className="font-semibold mb-2">[개인정보 수집 및 이용 동의]</h4>
                                <p><strong>Ⅰ. 수집 목적:</strong> 재화 또는 서비스 제공 물품배송, 서비스 제공, 계약서・청구서 발송, 광고 콘텐츠 제공, 맞춤서비스 제공, 요금결제・정산</p>
                                <p><strong>Ⅱ. 수집 항목:</strong> 성명, 소속, 전화번호, 이메일, 구매내역</p>
                                <p><strong>Ⅲ. 보유 및 이용기간:</strong> 수집일부터 5년까지</p>
                                <p><strong>Ⅳ.</strong> 귀하는 위와 같이 개인정보를 수집 및 이용하는데 동의를 거부할 권리가 있습니다. 단 미동의시 상담 및 배송 서비스관련 업무에 불이익을 받을 수 있습니다.</p>
                              </div>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                      
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="privacy-agreement"
                          checked={privacyAgreed}
                          onCheckedChange={(checked) => setPrivacyAgreed(checked as boolean)}
                        />
                        <Label 
                          htmlFor="privacy-agreement" 
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          개인정보 수집 및 이용에 동의합니다.
                        </Label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Captcha */}
              <div className="flex justify-center">
                <HCaptcha
                  key={`consultation-captcha-${activeForm}`}
                  sitekey="9382f96a-a74c-480c-bb96-194e5792590a"
                  onVerify={(token) => setConsultationCaptchaToken(token)}
                  onExpire={() => setConsultationCaptchaToken(null)}
                  onError={() => setConsultationCaptchaToken(null)}
                />
              </div>
                  
                  {isRateLimited && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800 font-medium">⚠️ {rateLimitMessage}</p>
                    </div>
                  )}
                  <Button type="submit" className="w-full" size="lg" disabled={isSubmitting || !privacyAgreed || isRateLimited || !consultationCaptchaToken}>
                    {isSubmitting ? "전송 중..." : isRateLimited ? "제출 제한됨" : "상담 신청하기"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          ) : (
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="text-2xl">견적 요청</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleQuoteSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="quote-name">이름 <span className="text-destructive">*</span></Label>
                    <Input 
                      id="quote-name"
                      placeholder="ex) 홍길동" 
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="mt-2"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="quote-institution">회사 및 기관명 <span className="text-destructive">*</span></Label>
                    <Input 
                      id="quote-institution"
                      placeholder="ex) OO 대학교" 
                      value={formData.institution}
                      onChange={(e) => handleInputChange('institution', e.target.value)}
                      className="mt-2"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="quote-pi">PI 성함 <span className="text-destructive">*</span></Label>
                    <Input 
                      id="quote-pi"
                      placeholder="ex) OOO 교수님" 
                      value={formData.pi_name}
                      onChange={(e) => handleInputChange('pi_name', e.target.value)}
                      className="mt-2"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="quote-email">이메일주소 <span className="text-destructive">*</span></Label>
                    <Input 
                      id="quote-email"
                      type="email" 
                      placeholder="ex) info@centraltg.co.kr" 
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="mt-2"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="quote-mobile">연락처(휴대폰) <span className="text-destructive">*</span></Label>
                      <Input 
                        id="quote-mobile"
                        placeholder="ex) 010-1234-5678" 
                        value={formData.mobile_phone}
                        onChange={(e) => handleInputChange('mobile_phone', e.target.value)}
                        className="mt-2"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="quote-landline">연락처(회사)</Label>
                      <Input 
                        id="quote-landline"
                        placeholder="ex) 02-1234-5678" 
                        value={formData.landline_phone}
                        onChange={(e) => handleInputChange('landline_phone', e.target.value)}
                        className="mt-2"
                      />
                    </div>
                  </div>
                  
                  <div className="border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-semibold">실험동물 정보</h4>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addStrain}
                        className="text-sm"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        계통 추가
                      </Button>
                    </div>
                    
                    <div className="space-y-6">
                      {animalStrains.map((strain, index) => (
                        <div key={index} className="p-4 border rounded-lg bg-muted/20">
                          <div className="flex items-center justify-between mb-4">
                            <h5 className="font-medium">계통 {index + 1}</h5>
                            {animalStrains.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeStrain(index)}
                                className="text-destructive hover:bg-destructive/10"
                              >
                                <Minus className="h-4 w-4 mr-1" />
                                삭제
                              </Button>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 gap-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor={`strain-name-${index}`}>① 계통명 <span className="text-destructive">*</span></Label>
                                <Input 
                                  id={`strain-name-${index}`}
                                  placeholder="ex) C57BL/6J" 
                                  value={strain.strain_name}
                                  onChange={(e) => handleStrainChange(index, 'strain_name', e.target.value)}
                                  required
                                />
                              </div>
                              <div>
                                <Label htmlFor={`strain-number-${index}`}>② 계통번호 <span className="text-destructive">*</span></Label>
                                <Input 
                                  id={`strain-number-${index}`}
                                  placeholder="ex) 000664" 
                                  value={strain.strain_number}
                                  onChange={(e) => handleStrainChange(index, 'strain_number', e.target.value)}
                                  required
                                />
                              </div>
                            </div>
                            
                            <div>
                              <Label>③ 성별</Label>
                              <RadioGroup 
                                value={strain.gender} 
                                onValueChange={(value) => handleStrainChange(index, 'gender', value)}
                                className="flex gap-4 mt-2"
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="male" id={`male-${index}`} />
                                  <Label htmlFor={`male-${index}`}>수컷</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="female" id={`female-${index}`} />
                                  <Label htmlFor={`female-${index}`}>암컷</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="both" id={`both-${index}`} />
                                  <Label htmlFor={`both-${index}`}>혼합</Label>
                                </div>
                              </RadioGroup>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label htmlFor={`age-${index}`}>④ 수령 희망 주령</Label>
                                <Input 
                                  id={`age-${index}`}
                                  placeholder="ex) 8주령, 6-8주령" 
                                  value={strain.age_weeks}
                                  onChange={(e) => handleStrainChange(index, 'age_weeks', e.target.value)}
                                />
                              </div>
                              
                              <div>
                                <Label htmlFor={`quantity-${index}`}>⑤ 마리 수</Label>
                                <Input 
                                  id={`quantity-${index}`}
                                  type="number" 
                                  placeholder="ex) 20" 
                                  value={strain.quantity}
                                  onChange={(e) => handleStrainChange(index, 'quantity', e.target.value)}
                                />
                              </div>
                            </div>
                            
                            <div>
                              <Label htmlFor={`delivery-date-${index}`}>⑥ 희망 납품일</Label>
                              <Input 
                                id={`delivery-date-${index}`}
                                type="date" 
                                value={strain.delivery_date}
                                onChange={(e) => handleStrainChange(index, 'delivery_date', e.target.value)}
                              />
                            </div>
                            
                            <div>
                              <Label htmlFor={`additional-${index}`}>추가 요청사항 (선택)</Label>
                              <Textarea 
                                id={`additional-${index}`}
                                placeholder="추가 요구사항을 기재해 주시기 바랍니다..." 
                                className="min-h-[60px]"
                                value={strain.additional_notes}
                                onChange={(e) => handleStrainChange(index, 'additional_notes', e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="border-t pt-6">
                    <div className="space-y-4">
                      <Label htmlFor="file-upload">파일 첨부 (선택)</Label>
                      <div className="space-y-3">
                        <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg border">
                          <p className="font-medium mb-2">파일 업로드 제한사항</p>
                          <ul className="space-y-1 list-disc list-inside">
                            <li><strong>파일 개수:</strong> 최대 5개</li>
                            <li><strong>파일 크기:</strong> 각 파일당 최대 10MB</li>
                          </ul>
                        </div>
                        {attachments.length === 0 ? (
                          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
                            <Upload className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                            <div className="space-y-2">
                              <p className="text-sm text-muted-foreground">
                                파일을 끌어다 놓거나 클릭하여 업로드하세요
                              </p>
                            </div>
                            <Input
                              id="file-upload"
                              type="file"
                              multiple
                              className="hidden"
                              onChange={handleFileChange}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              className="mt-4"
                              onClick={() => document.getElementById('file-upload')?.click()}
                              disabled={isUploading}
                            >
                              파일 선택
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {attachments.map((file, index) => (
                              <div key={index} className="flex items-center justify-between p-3 border rounded-lg bg-muted/50">
                                <div className="flex items-center space-x-3">
                                  <div className="p-2 bg-primary/10 rounded">
                                    <Upload className="h-4 w-4 text-primary" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium">{file.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {(file.size / 1024 / 1024).toFixed(2)} MB
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeFile(index)}
                                  disabled={isUploading}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                            {attachments.length < 5 && (
                              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 text-center">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => document.getElementById('file-upload')?.click()}
                                  disabled={isUploading}
                                >
                                  <Upload className="h-4 w-4 mr-2" />
                                  파일 추가 ({attachments.length}/5)
                                </Button>
                                <Input
                                  id="file-upload"
                                  type="file"
                                  multiple
                                  className="hidden"
                                  onChange={handleFileChange}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t pt-6">
                    <div className="space-y-4">
                      <Collapsible open={isPolicyOpen} onOpenChange={setIsPolicyOpen}>
                        <CollapsibleTrigger asChild>
                          <Button 
                            type="button" 
                            variant="outline" 
                            className="w-full justify-between"
                          >
                            개인정보 수집 및 이용 동의 / 처리방침
                            {isPolicyOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-4">
                          <div className="max-h-64 overflow-y-auto p-4 border rounded-md bg-muted/50 text-sm">
                            <div className="space-y-4">
                              <div>
                                <h4 className="font-semibold mb-2">[개인정보 수집 및 이용 동의]</h4>
                                <p><strong>Ⅰ. 수집 목적:</strong> 재화 또는 서비스 제공 물품배송, 서비스 제공, 계약서・청구서 발송, 광고 콘텐츠 제공, 맞춤서비스 제공, 요금결제・정산</p>
                                <p><strong>Ⅱ. 수집 항목:</strong> 성명, 소속, 전화번호, 이메일, 구매내역</p>
                                <p><strong>Ⅲ. 보유 및 이용기간:</strong> 수집일부터 5년까지</p>
                                <p><strong>Ⅳ.</strong> 귀하는 위와 같이 개인정보를 수집 및 이용하는데 동의를 거부할 권리가 있습니다. 단 미동의시 상담 및 배송 서비스관련 업무에 불이익을 받을 수 있습니다.</p>
                              </div>
                              
                              <div>
                                <h4 className="font-semibold mb-2">[Central TG Inc. 개인정보 처리방침]</h4>
                                <p className="mb-2">Central TG Inc. 은(는) 정보주체의 자유와 권리 보호를 위해 「개인정보 보호법」 및 관계 법령이 정한 바를 준수하여, 적법하게 개인정보를 처리하고 안전하게 관리하고 있습니다.</p>
                                
                                <div className="space-y-2">
                                  <p><strong>Ⅰ. 개인정보의 처리목적</strong></p>
                                  <p>Central TG Inc. 은(는) 재화 또는 서비스 제공 물품배송, 서비스 제공, 계약서・청구서 발송, 광고 콘텐츠 제공, 맞춤서비스 제공, 요금결제・정산의 목적으로 개인정보를 처리합니다.</p>
                                  
                                  <p><strong>Ⅱ. 처리하는 개인정보 항목</strong></p>
                                  <p>Central TG Inc. 은(는) 서비스 제공을 위해 필요 최소한의 범위에서 개인정보를 수집・이용합니다.</p>
                                  <p>수집・이용 항목: 성명, 소속, 전화번호, 이메일, 구매내역</p>
                                  
                                  <p><strong>Ⅲ. 개인정보의 처리 및 보유기간</strong></p>
                                  <p>재화 또는 서비스 제공: 재화·서비스 공급완료 후 5년</p>
                                  
                                  <p><strong>Ⅳ. 개인정보의 파기 절차 및 방법</strong></p>
                                  <p>Central TG Inc. 은(는) 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가 불필요하게 되었을 때에는 지체없이 해당 개인정보를 파기합니다.</p>
                                  
                                  <p><strong>Ⅴ. 개인정보의 안전성 확보조치</strong></p>
                                  <p>관리적 조치: 내부관리계획 수립・시행, 전담조직 운영, 정기적 직원 교육</p>
                                  <p>기술적 조치: 개인정보처리시스템 등의 접근권한 관리, 개인정보의 암호화</p>
                                  
                                  <p><strong>Ⅵ. 개인정보 보호책임자</strong></p>
                                  <p>성명: 이수영 (대리)</p>
                                  <p>연락처: 전화번호(02-3461-5255), 이메일(info@centraltg.co.kr)</p>
                                  <p>부서명: 영업지원팀</p>
                                  
                                  <p><strong>Ⅶ. 개인정보 처리방침의 변경</strong></p>
                                  <p>이 개인정보 처리방침은 2024.09.05 부터 적용됩니다.</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                      
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="privacy-agreement"
                          checked={privacyAgreed}
                          onCheckedChange={(checked) => setPrivacyAgreed(checked as boolean)}
                        />
                        <Label 
                          htmlFor="privacy-agreement" 
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          개인정보 수집 및 이용에 동의합니다.
                        </Label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Captcha */}
                <div className="flex justify-center">
                  <HCaptcha
                    key={`quote-captcha-${activeForm}`}
                    sitekey="9382f96a-a74c-480c-bb96-194e5792590a"
                    onVerify={(token) => setQuoteCaptchaToken(token)}
                    onExpire={() => setQuoteCaptchaToken(null)}
                    onError={() => setQuoteCaptchaToken(null)}
                  />
                </div>
                  
                  {isRateLimited && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800 font-medium">⚠️ {rateLimitMessage}</p>
                    </div>
                  )}
                  <Button type="submit" className="w-full" size="lg" disabled={isSubmitting || !privacyAgreed || isRateLimited || !quoteCaptchaToken}>
                    {isSubmitting ? "전송 중..." : isRateLimited ? "제출 제한됨" : "견적 요청 보내기"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
